import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useState } from "react";
import { useAuth, useCart, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

export default function ProductCard({ p }) {
  const { user, login } = useAuth();
  const { wishlist, refreshWishlist } = useCart();
  const isWished = wishlist.some(w => w.product_id === p.product_id);

  // Track image/video load state so we can show a warm shimmer skeleton until the
  // asset paints — prevents jarring blank tiles on slow connections.
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const rawSrc = p.images?.[0] || "";
  const hasMedia = !!rawSrc.trim();
  const isVideo = hasMedia && /\.(mp4|mov|webm|m4v)(\?|$)/i.test(rawSrc);

  const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
  const discount = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;

  const toggleWish = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { login(); return; }
    await axios.post(`${API}/wishlist/toggle`, { product_id: p.product_id });
    refreshWishlist();
    toast.success(isWished ? "Removed from wishlist" : "Added to wishlist");
  };

  return (
    <Link to={`/product/${p.product_id}`} className="product-card block bg-white rounded-2xl overflow-hidden border border-[#EAE3D9]" data-testid={`product-card-${p.product_id}`}>
      <div className="relative aspect-square bg-[#FFF5F3] hover-zoom-wrap">
        {/* Shimmer skeleton — sits underneath the media until it fully loads */}
        {hasMedia && !mediaLoaded && (
          <div
            className="absolute inset-0 skeleton-shimmer"
            data-testid={`product-card-skeleton-${p.product_id}`}
            aria-hidden="true"
          />
        )}
        {!hasMedia ? (
          // Decorative fallback for products uploaded without a photo yet —
          // better than an empty <img> with a broken-image icon.
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#FFF5F3] to-[#F5E1D6] text-[#C5A059]" data-testid={`product-card-noimg-${p.product_id}`}>
            <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.1">
              <path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" />
            </svg>
            <span className="text-[11px] uppercase tracking-widest mt-2 text-[#8A8480]">Photo coming soon</span>
          </div>
        ) : isVideo ? (
          <video
            src={rawSrc}
            className={`w-full h-full object-cover transition-opacity duration-500 ${mediaLoaded ? "opacity-100" : "opacity-0"}`}
            muted loop playsInline autoPlay
            onLoadedData={() => setMediaLoaded(true)}
            onError={() => setMediaLoaded(true)}
          />
        ) : (
          <img
            src={rawSrc}
            alt={p.name}
            className={`w-full h-full object-cover transition-opacity duration-500 ${mediaLoaded ? "opacity-100" : "opacity-0"}`}
            loading="lazy"
            decoding="async"
            onLoad={() => setMediaLoaded(true)}
            onError={() => setMediaLoaded(true)}
          />
        )}
        <button onClick={toggleWish}
          className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/95 flex items-center justify-center shadow-sm hover:scale-110 transition-transform"
          data-testid={`wishlist-toggle-${p.product_id}`}>
          <Heart className={`h-4 w-4 ${isWished ? "fill-[#8B1E28] text-[#8B1E28]" : "text-[#595552]"}`} />
        </button>
        {discount > 0 && (
          <div className="absolute top-3 left-3 bg-[#8B1E28] text-white text-[10px] font-medium px-2 py-1 rounded-full label-small">
            {discount}% off
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] label-small text-[#C5A059]">{p.purity}{p.hide_weight ? "" : ` · ${p.weight}g`}</span>
          {p.collection && <span className="text-[10px] label-small text-[#8A8480]">{p.collection}</span>}
        </div>
        <h3 className="font-display text-lg text-[#2C2A29] leading-tight line-clamp-1">{p.name}</h3>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-base font-semibold text-[#2C2A29]">{inr(p.price)}</span>
          {p.mrp > p.price && (
            <span className="text-xs text-[#8A8480] line-through">{inr(p.mrp)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
